import { AsyncPipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Subject, catchError, finalize, of, takeUntil } from 'rxjs';

import { SkeletonModule } from 'primeng/skeleton';

import { ClientService, ToastMessageService } from '@fitness-force/api';

export interface GuestPassSummary {
  service_sessions: number;
  service_sessions_booked: number;
  service_sessions_used: number;
  service_sessions_noshow: number;
  available_sessions: number;
}

@Component({
  selector: 'guest-pass-summary',
  templateUrl: './guest-pass-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SkeletonModule,
    TranslateModule,
    AsyncPipe,
    DecimalPipe,
  ],
})
export class GuestPassSummaryComponent implements OnInit, OnDestroy {
  private readonly clientService = inject(ClientService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly clientId = input(0);
  readonly tenantId = input(0);
  readonly subscriptionId = input(0);

  summary$ = new BehaviorSubject<GuestPassSummary | null>(null);
  loading = false;

  ngOnInit(): void {
    this.loadSummary();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSummary(): void {
    const clientId = this.clientId();
    const subscriptionId = this.subscriptionId();
    if (!clientId || !subscriptionId) return;

    this.loading = true;
    this.clientService.getGuestPassSummary(clientId, subscriptionId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.toastMessageService.showError(error);
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        })
      )
      .subscribe({
        next: (response: GuestPassSummary | null) => {
          this.summary$.next(response);
        }
      });
  }
}
